import React, { useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import { useLocationContext } from "context/LocationContext";
import { YEAR_OPTIONS } from "./monitoringConstants";
import {
  createWriteUp,
  getAssessmentsByYearAndParentId,
  getAllWriteUps,
  getConsolidatedAssessmentsByYear,
  getWriteUpByUniqueConstraint,
  getWriteUpByYearAndLocation,
  updateWriteUp,
} from "service/contract-assessment.service";
import { toast } from "sonner";
import useAuth from "hooks/useAuth";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { classifyPerformancePercent, getRegions } from "utils/utils";
import QuillHtmlDisplay from "components/QuillHtmlDisplay";

const ALL_REGIONS_OPTION = { label: "All Regions", value: "all" };

const CHART_COLORS = {
  excellent: "#16a34a",
  veryGood: "#0ea5e9",
  good: "#eab308",
  satisfactory: "#d6b8a3",
  unsatisfactory: "#ef4444",
};

function flattenOrgTree(node, rows = []) {
  if (!node) return rows;
  rows.push(node);
  (node.children || []).forEach((child) => flattenOrgTree(child, rows));
  return rows;
}

function normalizePercent(rawScore) {
  const numeric = Number(rawScore);
  if (Number.isNaN(numeric)) return 0;
  if (numeric <= 4) return numeric * 25;
  return numeric;
}

/** Classify using the same % scale as normalizePercent (OHLGS scores may be 0–4, e.g. 3.75 → 93.75%). */
function classifyOhlgsScore(rawScore) {
  return classifyPerformancePercent(normalizePercent(rawScore));
}

function toOrdinal(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "-";
  const mod100 = numeric % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${numeric}th`;
  const mod10 = numeric % 10;
  if (mod10 === 1) return `${numeric}st`;
  if (mod10 === 2) return `${numeric}nd`;
  if (mod10 === 3) return `${numeric}rd`;
  return `${numeric}th`;
}

function rankToNumber(value) {
  const numeric = Number(String(value || "").replace(/\D/g, ""));
  if (!Number.isFinite(numeric) || numeric <= 0) return Number.POSITIVE_INFINITY;
  return numeric;
}

function safeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.regions)) return payload.regions;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.regions)) return payload.data.regions;
  return [];
}

function regionTotalScore(row) {
  if (row?.totalScore !== undefined && row?.totalScore !== null) return row.totalScore;
  return row?.regionAverageScore ?? 0;
}

function resolveRegionCode(region) {
  return String(region?.regionCode || region?.locationCode || region?.parentId || "");
}

function resolveOrgNodeCode(node) {
  return String(node?.orgUnitId || node?.locationCode || node?.regionCode || node?.parentId || "");
}

function isAoRegion(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "ao" || normalized === "ao region";
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function regionAbbreviation(regionName) {
  const clean = String(regionName || "").replace(/\s+Region$/i, "").trim();
  if (!clean) return "-";
  return clean
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function performanceClass(performance) {
  if (performance === "Excellent") return "bg-emerald-500/20 text-emerald-700";
  if (performance === "Very Good") return "bg-sky-500/20 text-sky-700";
  if (performance === "Good") return "bg-yellow-400/30 text-yellow-800";
  if (performance === "Satisfactory") return "bg-orange-400/20 text-orange-700";
  return "bg-red-500/20 text-red-700";
}

function sourceLocationName(row, orgIndex) {
  const code = String(row?.locationCode || "");
  const locationNode = orgIndex[code];
  if (locationNode?.name) {
    const level = String(row?.locationType || "").toUpperCase();
    if (level === "DISTRICT") return `${locationNode.name} (District)`;
    if (level === "REGIONAL") return `${locationNode.name} (Region)`;
    return locationNode.name;
  }
  return code || "-";
}

function hasHtmlContent(value) {
  return /<[^>]+>/.test(String(value || ""));
}

function NarrativeFieldCard({ label, value }) {
  const content = String(value || "").trim();

  return (
    <div className="flex h-full flex-col rounded-lg border border-border bg-card p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</p>
      <div className="mt-3 flex-1 text-left text-sm leading-6 text-foreground">
        {!content ? (
          <span className="text-muted-foreground">—</span>
        ) : hasHtmlContent(content) ? (
          <QuillHtmlDisplay
            html={content}
            className="[&_.quill-html-display-wrap]:border-0 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-0 [&_.ql-editor]:p-0 [&_.ql-editor]:text-left [&_.ql-editor]:leading-6"
          />
        ) : (
          <p className="whitespace-pre-wrap break-words text-left">{content}</p>
        )}
      </div>
    </div>
  );
}

function RegionalMvSummaryCards({ regionName, writeUp }) {
  return (
    <div className="rounded-xl border border-border bg-muted/10 p-4 md:p-5">
      <div className="mb-4 border-b border-border pb-3">
        <h4 className="text-sm font-semibold text-foreground">{regionName}</h4>
        <p className="mt-1 text-xs text-muted-foreground">Regional monitoring &amp; verification summary</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <NarrativeFieldCard label="Scope of Assessment" value={writeUp?.scopeOfAssessment} />
        <NarrativeFieldCard label="General Outcome" value={writeUp?.generalOutcome} />
        <NarrativeFieldCard label="Observations" value={writeUp?.generalComments} />
        <NarrativeFieldCard label="Recommendations" value={writeUp?.recommendations} />
      </div>
    </div>
  );
}

const CHAPTER4_NARRATIVE_CELL =
  "border border-border px-3 py-2 align-top text-left text-sm leading-6 break-words whitespace-pre-wrap min-w-[10rem] max-w-[20rem]";

function parseMvTeamRows(rawValue) {
  if (!rawValue) return [];
  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((row) => ({
      teamComposition: row?.teamComposition || "",
      mmdasInRegionsMonitored: row?.mmdasInRegionsMonitored || "",
      rccsMonitored: row?.rccsMonitored || "",
    }));
  } catch (_error) {
    return [];
  }
}

function isConflictError(error) {
  const status = Number(error?.response?.status || error?.status || 0);
  const message = String(error?.response?.data?.message || error?.message || "").toLowerCase();
  return status === 409 || message.includes("already exists");
}

export default function OhlgsConsolidatedChapterOne() {
  const { currentLocation, orgTree } = useLocationContext();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const defaultYear =
    YEAR_OPTIONS.find((option) => option.value === String(currentYear)) ||
    YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedRegion, setSelectedRegion] = useState(ALL_REGIONS_OPTION);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [generalIssues, setGeneralIssues] = useState("");
  const [dagiNarrative, setDagiNarrative] = useState("");
  const [introductionNarrative, setIntroductionNarrative] = useState("");
  const [chapterTwoMainNarrative, setChapterTwoMainNarrative] = useState("");
  const [phaseOneNarrative, setPhaseOneNarrative] = useState("");
  const [phaseTwoNarrative, setPhaseTwoNarrative] = useState("");
  const [phaseThreeNarrative, setPhaseThreeNarrative] = useState("");
  const [phaseFourNarrative, setPhaseFourNarrative] = useState("");
  const [decisionMakingIntroduction, setDecisionMakingIntroduction] = useState("");
  const [guidingPrinciples, setGuidingPrinciples] = useState("");
  const [chapterSixIntroduction, setChapterSixIntroduction] = useState("");
  const [chapterSixGuidingPrinciples, setChapterSixGuidingPrinciples] = useState("");
  const [chapterThreeIntro, setChapterThreeIntro] = useState("");
  const [chapterThreeObjective, setChapterThreeObjective] = useState("");
  const [chapterThreeScopeMethodology, setChapterThreeScopeMethodology] = useState("");
  const [chapterFourSummary, setChapterFourSummary] = useState("");
  const [chapterFourScopeOfAssignment, setChapterFourScopeOfAssignment] = useState("");
  const [chapterFourGeneralOutcome, setChapterFourGeneralOutcome] = useState("");
  const [chapterFourObservation, setChapterFourObservation] = useState("");
  const [chapterFourGeneralRecommendation, setChapterFourGeneralRecommendation] = useState("");
  const [chapterFourConclusion, setChapterFourConclusion] = useState("");
  const [mvTeamRows, setMvTeamRows] = useState([
    { teamComposition: "", mmdasInRegionsMonitored: "", rccsMonitored: "" },
  ]);
  const [executiveSummaryWriteUpId, setExecutiveSummaryWriteUpId] = useState(0);
  const [generalIssuesWriteUpId, setGeneralIssuesWriteUpId] = useState(0);
  const [dagiWriteUpId, setDagiWriteUpId] = useState(0);
  const [introductionWriteUpId, setIntroductionWriteUpId] = useState(0);
  const [chapterTwoMainWriteUpId, setChapterTwoMainWriteUpId] = useState(0);
  const [phaseOneWriteUpId, setPhaseOneWriteUpId] = useState(0);
  const [phaseTwoWriteUpId, setPhaseTwoWriteUpId] = useState(0);
  const [phaseThreeWriteUpId, setPhaseThreeWriteUpId] = useState(0);
  const [phaseFourWriteUpId, setPhaseFourWriteUpId] = useState(0);
  const [chapterThreeIntroWriteUpId, setChapterThreeIntroWriteUpId] = useState(0);
  const [chapterThreeTableWriteUpId, setChapterThreeTableWriteUpId] = useState(0);
  const [chapterThreeObjectiveWriteUpId, setChapterThreeObjectiveWriteUpId] = useState(0);
  const [chapterThreeScopeMethodologyWriteUpId, setChapterThreeScopeMethodologyWriteUpId] = useState(0);
  const [chapterFourSummaryWriteUpId, setChapterFourSummaryWriteUpId] = useState(0);
  const [chapterFourScopeWriteUpId, setChapterFourScopeWriteUpId] = useState(0);
  const [chapterFourOutcomeWriteUpId, setChapterFourOutcomeWriteUpId] = useState(0);
  const [chapterFourObservationWriteUpId, setChapterFourObservationWriteUpId] = useState(0);
  const [chapterFourRecommendationWriteUpId, setChapterFourRecommendationWriteUpId] = useState(0);
  const [chapterFourConclusionWriteUpId, setChapterFourConclusionWriteUpId] = useState(0);
  const [chapterSixIntroductionWriteUpId, setChapterSixIntroductionWriteUpId] = useState(0);
  const [chapterSixGuidingPrinciplesWriteUpId, setChapterSixGuidingPrinciplesWriteUpId] = useState(0);
  const [isSavingWriteUps, setIsSavingWriteUps] = useState(false);
  const [savingSections, setSavingSections] = useState({});
  const [activeChapter, setActiveChapter] = useState(1);
  const [viewAllReport, setViewAllReport] = useState(false);
  const [consolidatedRows, setConsolidatedRows] = useState([]);
  const [districtRowsByRegion, setDistrictRowsByRegion] = useState({});
  const [chapterFourRows, setChapterFourRows] = useState([]);
  const [regionalWriteUpsByCode, setRegionalWriteUpsByCode] = useState({});
  const reportContainerRef = useRef(null);

  const setWriteUpIdForSection = (sectionCommented, writeUpId) => {
    const resolvedId = Number(writeUpId || 0);
    if (!resolvedId) return;
    if (sectionCommented === "OHLGS_CONSOLIDATED_GENERAL_ISSUES") setGeneralIssuesWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_DAGI") setDagiWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_EXECUTIVE_SUMMARY") setExecutiveSummaryWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_INTRODUCTION") setIntroductionWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_MAIN") setChapterTwoMainWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE1") setPhaseOneWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE2") setPhaseTwoWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE3") setPhaseThreeWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE4") setPhaseFourWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_INTRO") setChapterThreeIntroWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_MV_TEAMS_TABLE") setChapterThreeTableWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_OBJECTIVE") setChapterThreeObjectiveWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_SCOPE_METHODOLOGY") setChapterThreeScopeMethodologyWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_SUMMARY") setChapterFourSummaryWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_SCOPE_OF_ASSIGNMENT") setChapterFourScopeWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_OUTCOME") setChapterFourOutcomeWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_OBSERVATION") setChapterFourObservationWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_RECOMMENDATION") setChapterFourRecommendationWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_CONCLUSION") setChapterFourConclusionWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER6_INTRODUCTION") setChapterSixIntroductionWriteUpId(resolvedId);
    if (sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER6_GUIDING_PRINCIPLES") setChapterSixGuidingPrinciplesWriteUpId(resolvedId);
  };

  const resolveExistingWriteUpId = async (year, sectionCommented) => {
    const response = await getWriteUpByUniqueConstraint({
      locationCode: "NATIONAL",
      year,
      sectionCommented,
    });
    const data = response?.data?.data || response?.data || null;
    if (Array.isArray(data)) {
      const match = data.find((row) => String(row?.sectionCommented || "") === String(sectionCommented));
      return Number(match?.writeUpId || match?.id || 0);
    }
    return Number(data?.writeUpId || data?.id || 0);
  };

  const saveSingleNarrativeWriteUp = async ({ id, sectionCommented, title, comments }) => {
    if (!selectedYear?.value) return;
    const username = user?.user?.username || "system";
    const fullName = user?.user?.fullName || username;
    const userRole = user?.user?.role || "national_officer";
    const year = String(selectedYear.value);

    const basePayload = {
      writeUpId: id || 0,
      username,
      fullName,
      userRole,
      type: "OHLGS-CONS",
      title,
      locationCode: "NATIONAL",
      year,
      sectionCommented,
      comments: comments || "",
      createdBy: username,
    };

    const preferredId = Number(id || 0);
    if (preferredId > 0) {
      await updateWriteUp({
        id: preferredId,
        payload: {
          ...basePayload,
          writeUpId: preferredId,
        },
      });
      setWriteUpIdForSection(sectionCommented, preferredId);
      return { mode: "updated", writeUpId: preferredId };
    }

    try {
      const created = await createWriteUp(basePayload);
      const createdId = Number(created?.data?.writeUpId || created?.data?.data?.writeUpId || 0);
      if (createdId) setWriteUpIdForSection(sectionCommented, createdId);
      return { mode: "created", writeUpId: createdId };
    } catch (itemError) {
      if (!isConflictError(itemError)) throw itemError;
      const existingId = await resolveExistingWriteUpId(year, sectionCommented);
      if (!existingId) throw itemError;
      await updateWriteUp({
        id: existingId,
        payload: {
          ...basePayload,
          writeUpId: existingId,
        },
      });
      setWriteUpIdForSection(sectionCommented, existingId);
      return { mode: "updated", writeUpId: existingId };
    }
  };

  const handleSaveSection = async (item, { showToast = true } = {}) => {
    setSavingSections((current) => ({ ...current, [item.sectionCommented]: true }));
    try {
      const result = await saveSingleNarrativeWriteUp(item);
      if (showToast) {
        toast.success(
          result?.mode === "updated"
            ? "Status Code: 200 OK. Narrative updated successfully."
            : "Status Code: 201 Created. Narrative saved successfully.",
        );
      }
    } catch (error) {
      console.error("Failed to save consolidated write-up section:", error);
      if (showToast) toast.error(error?.response?.data?.message || "Failed to send narrative");
      throw error;
    } finally {
      setSavingSections((current) => ({ ...current, [item.sectionCommented]: false }));
    }
  };

  const renderSendSectionButton = (item) => {
    const isSending = !!savingSections[item.sectionCommented];
    return (
      <div className="mt-3 flex justify-end print-hide">
        <Button type="button" variant="outline" onClick={() => handleSaveSection(item)} disabled={isSending || isSavingWriteUps}>
          {isSending ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Send" className="h-4 w-4" />}
          {isSending ? "Saving..." : "Save"}
        </Button>
      </div>
    );
  };

  const orgIndex = useMemo(() => {
    const allNodes = flattenOrgTree(orgTree);
    const byOrgUnitId = {};
    allNodes.forEach((node) => {
      if (node?.orgUnitId) byOrgUnitId[String(node.orgUnitId)] = node;
    });
    return byOrgUnitId;
  }, [orgTree]);

  const regionOptions = useMemo(
    () => [
      ALL_REGIONS_OPTION,
      ...getRegions()
        .map((region) => ({
          label: region.displayName,
          value: region.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [],
  );

  const filteredConsolidatedRows = useMemo(() => {
    if (!selectedRegion?.value || selectedRegion.value === "all") return consolidatedRows;
    return consolidatedRows.filter((region) => resolveRegionCode(region) === selectedRegion.value);
  }, [consolidatedRows, selectedRegion]);

  const filteredDistrictRowsByRegion = useMemo(() => {
    if (!selectedRegion?.value || selectedRegion.value === "all") return districtRowsByRegion;
    const regionCode = selectedRegion.value;
    if (!districtRowsByRegion[regionCode]) return {};
    return { [regionCode]: districtRowsByRegion[regionCode] };
  }, [districtRowsByRegion, selectedRegion]);

  const filteredChapterFourRows = useMemo(() => {
    if (!selectedRegion?.value || selectedRegion.value === "all") return chapterFourRows;
    return chapterFourRows.filter((row) => {
      const level = String(row?.locationType || "").toUpperCase();
      const locationCode = String(row?.locationCode || "");
      const fallbackRegionCode = String(row?._sourceRegionCode || "");
      const regionCode = level === "REGIONAL" ? locationCode : fallbackRegionCode;
      return regionCode === selectedRegion.value;
    });
  }, [chapterFourRows, selectedRegion]);

  const regionRows = useMemo(
    () =>
      filteredConsolidatedRows.map((region) => {
        const displayName = orgIndex[String(region.regionCode)]?.name || region.regionCode;
        return { ...region, displayName };
      }),
    [filteredConsolidatedRows, orgIndex],
  );

  const rankedRegionRows = useMemo(
    () =>
      [...regionRows]
        .sort((a, b) => {
          const rankDiff = rankToNumber(a?.regionRank) - rankToNumber(b?.regionRank);
          if (rankDiff !== 0) return rankDiff;
          return normalizePercent(regionTotalScore(b)) - normalizePercent(regionTotalScore(a));
        })
        .map((row, index) => ({
          ...row,
          rankNumber: Number(row?.regionRank) > 0 ? Number(row.regionRank) : index + 1,
          computedRank: toOrdinal(Number(row?.regionRank) > 0 ? Number(row.regionRank) : index + 1),
        })),
    [regionRows],
  );

  const districtRows = useMemo(() => {
    const rows = [];
    rankedRegionRows.forEach((region) => {
      const regionCode = region.regionCode;
      const regionName = region.displayName;
      const districtsMap = filteredDistrictRowsByRegion[regionCode] || region.districts || {};
      Object.values(districtsMap).forEach((district) => {
        rows.push({
          regionCode,
          regionName,
          districtCode: district?.districtCode || "-",
          districtName: orgIndex[String(district?.districtCode)]?.name || district?.districtCode || "-",
          totalScore: district?.totalScore ?? 0,
          nationalRank: district?.nationalRank || "-",
          regionalRank: district?.regionalRank || "-",
          performance: classifyOhlgsScore(district?.totalScore),
        });
      });
    });
    return rows.sort((a, b) => {
      const scoreDiff = normalizePercent(b.totalScore) - normalizePercent(a.totalScore);
      if (scoreDiff !== 0) return scoreDiff;

      const rankDiff = rankToNumber(a.nationalRank) - rankToNumber(b.nationalRank);
      if (rankDiff !== 0) return rankDiff;

      return String(a.districtName || "").localeCompare(String(b.districtName || ""));
    });
  }, [rankedRegionRows, filteredDistrictRowsByRegion, orgIndex]);

  const chapterFourTablesByRegion = useMemo(() => {
    const groupedByRegion = {};
    filteredChapterFourRows.forEach((row) => {
      const level = String(row?.locationType || "").toUpperCase();
      const locationCode = String(row?.locationCode || "");
      const fallbackRegionCode = String(row?._sourceRegionCode || "");
      const regionCode = level === "REGIONAL" ? locationCode : fallbackRegionCode;
      if (!regionCode) return;

      if (!groupedByRegion[regionCode]) {
        groupedByRegion[regionCode] = {
          regionCode,
          regionName: orgIndex[regionCode]?.name || row?._sourceRegionName || regionCode,
          regionalRows: [],
          districtRowsByCode: {},
        };
      }

      if (level === "DISTRICT") {
        const districtCode = locationCode || "UNKNOWN";
        if (!groupedByRegion[regionCode].districtRowsByCode[districtCode]) {
          groupedByRegion[regionCode].districtRowsByCode[districtCode] = {
            districtCode,
            districtName: orgIndex[districtCode]?.name || districtCode,
            rows: [],
          };
        }
        groupedByRegion[regionCode].districtRowsByCode[districtCode].rows.push(row);
      } else {
        groupedByRegion[regionCode].regionalRows.push(row);
      }
    });

    const preferredRegionOrder = rankedRegionRows.map((row) => String(row.regionCode));
    const unorderedRegionCodes = Object.keys(groupedByRegion).filter((code) => !preferredRegionOrder.includes(code));
    const orderedRegionCodes = [...preferredRegionOrder, ...unorderedRegionCodes.sort((a, b) => a.localeCompare(b))];

    return orderedRegionCodes
      .filter((regionCode) => groupedByRegion[regionCode])
      .map((regionCode) => {
        const regionGroup = groupedByRegion[regionCode];
        const districts = Object.values(regionGroup.districtRowsByCode).sort((a, b) =>
          String(a.districtName || "").localeCompare(String(b.districtName || "")),
        );
        return {
          ...regionGroup,
          districts,
        };
      });
  }, [filteredChapterFourRows, rankedRegionRows, orgIndex]);

  const regionCodesFromOrgTree = useMemo(() => {
    const allNodes = flattenOrgTree(orgTree);
    const codes = allNodes
      .filter((node) => String(node?.level || "").toUpperCase().includes("REGION"))
      .filter((node) => !isAoRegion(node?.name))
      .map((node) => resolveOrgNodeCode(node))
      .filter(Boolean);
    return Array.from(new Set(codes));
  }, [orgTree]);

  const regionalCategoryCounts = useMemo(
    () =>
      rankedRegionRows.map((region) => {
        const districtsMap = filteredDistrictRowsByRegion[region.regionCode] || region.districts || {};
        const districts = Object.values(districtsMap);
        const counts = {
          excellent: 0,
          veryGood: 0,
          good: 0,
          satisfactory: 0,
          unsatisfactory: 0,
        };

        districts.forEach((district) => {
          const category = classifyOhlgsScore(district?.totalScore);
          if (category === "Excellent") counts.excellent += 1;
          else if (category === "Very Good") counts.veryGood += 1;
          else if (category === "Good") counts.good += 1;
          else if (category === "Satisfactory") counts.satisfactory += 1;
          else counts.unsatisfactory += 1;
        });

        return {
          regionCode: region.regionCode,
          regionName: region.displayName,
          ...counts,
          total: districts.length,
        };
      }),
    [rankedRegionRows, filteredDistrictRowsByRegion],
  );

  const topTenDistricts = useMemo(() => districtRows.slice(0, 10), [districtRows]);

  const unsatisfactoryDistricts = useMemo(
    () => districtRows.filter((row) => row.performance === "Unsatisfactory"),
    [districtRows],
  );

  const topThreeRccs = useMemo(() => rankedRegionRows.slice(0, 3), [rankedRegionRows]);

  const chapterFiveTotals = useMemo(
    () =>
      regionalCategoryCounts.reduce(
        (acc, row) => {
          acc.excellent += Number(row.excellent || 0);
          acc.veryGood += Number(row.veryGood || 0);
          acc.good += Number(row.good || 0);
          acc.satisfactory += Number(row.satisfactory || 0);
          acc.unsatisfactory += Number(row.unsatisfactory || 0);
          acc.total += Number(row.total || 0);
          return acc;
        },
        { excellent: 0, veryGood: 0, good: 0, satisfactory: 0, unsatisfactory: 0, total: 0 },
      ),
    [regionalCategoryCounts],
  );

  const chapterFiveBarChartData = useMemo(
    () =>
      regionalCategoryCounts.map((row) => ({
        region: regionAbbreviation(row.regionName),
        excellent: row.excellent,
        veryGood: row.veryGood,
        good: row.good,
        satisfactory: row.satisfactory,
        unsatisfactory: row.unsatisfactory,
      })),
    [regionalCategoryCounts],
  );

  const chapterFivePieData = useMemo(() => {
    const total = Number(chapterFiveTotals.total || 0);
    const toPercent = (value) => (total > 0 ? Number(((value / total) * 100).toFixed(0)) : 0);
    return [
      { name: "Excellent", value: chapterFiveTotals.excellent, percent: toPercent(chapterFiveTotals.excellent), color: CHART_COLORS.excellent },
      { name: "Very Good", value: chapterFiveTotals.veryGood, percent: toPercent(chapterFiveTotals.veryGood), color: CHART_COLORS.veryGood },
      { name: "Good", value: chapterFiveTotals.good, percent: toPercent(chapterFiveTotals.good), color: CHART_COLORS.good },
      { name: "Satisfactory", value: chapterFiveTotals.satisfactory, percent: toPercent(chapterFiveTotals.satisfactory), color: CHART_COLORS.satisfactory },
      { name: "Unsatisfactory", value: chapterFiveTotals.unsatisfactory, percent: toPercent(chapterFiveTotals.unsatisfactory), color: CHART_COLORS.unsatisfactory },
    ];
  }, [chapterFiveTotals]);

  const districtNationalPositionByCode = useMemo(() => {
    const map = {};
    districtRows.forEach((row, index) => {
      const key = String(row.districtCode || "");
      if (key) map[key] = index + 1;
    });
    return map;
  }, [districtRows]);

  const chapterFiveRegionBreakdown = useMemo(
    () =>
      rankedRegionRows.map((region) => {
        const regionDistrictRows = districtRows
          .filter((row) => row.regionCode === region.regionCode)
          .sort((a, b) => normalizePercent(b.totalScore) - normalizePercent(a.totalScore))
          .map((row, index) => ({
            ...row,
            regionalPosition: index + 1,
            nationalPosition: districtNationalPositionByCode[String(row.districtCode || "")] || null,
          }));

        const counts = {
          excellent: 0,
          veryGood: 0,
          good: 0,
          satisfactory: 0,
          unsatisfactory: 0,
        };
        regionDistrictRows.forEach((row) => {
          if (row.performance === "Excellent") counts.excellent += 1;
          else if (row.performance === "Very Good") counts.veryGood += 1;
          else if (row.performance === "Good") counts.good += 1;
          else if (row.performance === "Satisfactory") counts.satisfactory += 1;
          else counts.unsatisfactory += 1;
        });

        return {
          regionCode: region.regionCode,
          regionName: region.displayName,
          regionAbbrev: regionAbbreviation(region.displayName),
          districts: regionDistrictRows,
          counts,
          total: regionDistrictRows.length,
        };
      }),
    [rankedRegionRows, districtRows, districtNationalPositionByCode],
  );

  const loadNarrativeWriteUps = async (year) => {
    try {
      const rows = safeRows((await getAllWriteUps())?.data);
      const filtered = rows.filter(
        (row) =>
          String(row?.type || "") === "OHLGS-CONS" &&
          String(row?.year || "") === String(year) &&
          String(row?.locationCode || "").toUpperCase() === "NATIONAL",
      );

      const generalIssuesRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_GENERAL_ISSUES") || null;
      const dagiRow = filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_DAGI") || null;
      const summaryRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_EXECUTIVE_SUMMARY") || null;
      const introductionRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_INTRODUCTION") || null;
      const chapterTwoMainRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_MAIN") || null;
      const phaseOneRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE1") || null;
      const phaseTwoRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE2") || null;
      const phaseThreeRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE3") || null;
      const phaseFourRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER2_PHASE4") || null;
      const chapterThreeIntroRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_INTRO") || null;
      const chapterThreeTableRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_MV_TEAMS_TABLE") || null;
      const chapterThreeObjectiveRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_OBJECTIVE") || null;
      const chapterThreeScopeMethodologyRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER3_SCOPE_METHODOLOGY") || null;
      const chapterFourSummaryRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_SUMMARY") || null;
      const chapterFourScopeRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_SCOPE_OF_ASSIGNMENT") || null;
      const chapterFourOutcomeRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_OUTCOME") || null;
      const chapterFourObservationRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_OBSERVATION") || null;
      const chapterFourRecommendationRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_RECOMMENDATION") || null;
      const chapterFourConclusionRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER4_CONCLUSION") || null;
      const chapterSixIntroductionRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER6_INTRODUCTION") || null;
      const chapterSixGuidingPrinciplesRow =
        filtered.find((row) => row.sectionCommented === "OHLGS_CONSOLIDATED_CHAPTER6_GUIDING_PRINCIPLES") || null;

      setGeneralIssues(generalIssuesRow?.comments || "");
      setDagiNarrative(dagiRow?.comments || "");
      setExecutiveSummary(summaryRow?.comments || "");
      setIntroductionNarrative(introductionRow?.comments || "");
      setChapterTwoMainNarrative(chapterTwoMainRow?.comments || "");
      setPhaseOneNarrative(phaseOneRow?.comments || "");
      setPhaseTwoNarrative(phaseTwoRow?.comments || "");
      setPhaseThreeNarrative(phaseThreeRow?.comments || "");
      setPhaseFourNarrative(phaseFourRow?.comments || "");
      setChapterThreeIntro(chapterThreeIntroRow?.comments || "");
      setChapterThreeObjective(chapterThreeObjectiveRow?.comments || "");
      setChapterThreeScopeMethodology(chapterThreeScopeMethodologyRow?.comments || "");
      setChapterFourSummary(chapterFourSummaryRow?.comments || "");
      setChapterFourScopeOfAssignment(chapterFourScopeRow?.comments || "");
      setChapterFourGeneralOutcome(chapterFourOutcomeRow?.comments || "");
      setChapterFourObservation(chapterFourObservationRow?.comments || "");
      setChapterFourGeneralRecommendation(chapterFourRecommendationRow?.comments || "");
      setChapterFourConclusion(chapterFourConclusionRow?.comments || "");
      setChapterSixIntroduction(chapterSixIntroductionRow?.comments || "");
      setChapterSixGuidingPrinciples(chapterSixGuidingPrinciplesRow?.comments || "");
      setMvTeamRows(
        parseMvTeamRows(chapterThreeTableRow?.comments).length > 0
          ? parseMvTeamRows(chapterThreeTableRow?.comments)
          : [{ teamComposition: "", mmdasInRegionsMonitored: "", rccsMonitored: "" }],
      );
      setExecutiveSummaryWriteUpId(summaryRow?.writeUpId || 0);
      setGeneralIssuesWriteUpId(generalIssuesRow?.writeUpId || 0);
      setDagiWriteUpId(dagiRow?.writeUpId || 0);
      setIntroductionWriteUpId(introductionRow?.writeUpId || 0);
      setChapterTwoMainWriteUpId(chapterTwoMainRow?.writeUpId || 0);
      setPhaseOneWriteUpId(phaseOneRow?.writeUpId || 0);
      setPhaseTwoWriteUpId(phaseTwoRow?.writeUpId || 0);
      setPhaseThreeWriteUpId(phaseThreeRow?.writeUpId || 0);
      setPhaseFourWriteUpId(phaseFourRow?.writeUpId || 0);
      setChapterThreeIntroWriteUpId(chapterThreeIntroRow?.writeUpId || 0);
      setChapterThreeTableWriteUpId(chapterThreeTableRow?.writeUpId || 0);
      setChapterThreeObjectiveWriteUpId(chapterThreeObjectiveRow?.writeUpId || 0);
      setChapterThreeScopeMethodologyWriteUpId(chapterThreeScopeMethodologyRow?.writeUpId || 0);
      setChapterFourSummaryWriteUpId(chapterFourSummaryRow?.writeUpId || 0);
      setChapterFourScopeWriteUpId(chapterFourScopeRow?.writeUpId || 0);
      setChapterFourOutcomeWriteUpId(chapterFourOutcomeRow?.writeUpId || 0);
      setChapterFourObservationWriteUpId(chapterFourObservationRow?.writeUpId || 0);
      setChapterFourRecommendationWriteUpId(chapterFourRecommendationRow?.writeUpId || 0);
      setChapterFourConclusionWriteUpId(chapterFourConclusionRow?.writeUpId || 0);
      setChapterSixIntroductionWriteUpId(chapterSixIntroductionRow?.writeUpId || 0);
      setChapterSixGuidingPrinciplesWriteUpId(chapterSixGuidingPrinciplesRow?.writeUpId || 0);
    } catch (error) {
      console.error("Failed to load consolidated write-ups:", error);
    }
  };

  const handleLoad = async () => {
    if (!selectedYear?.value) return;
    setIsLoading(true);
    try {
      const year = selectedYear.value;
      const consolidatedResponse = await getConsolidatedAssessmentsByYear(year);
      const consolidatedData = safeRows(consolidatedResponse?.data);
      const filteredConsolidatedData = consolidatedData.filter((region) => {
        const regionCode = resolveRegionCode(region);
        const regionName = orgIndex[regionCode]?.name || region?.regionName || region?.name || regionCode;
        return !isAoRegion(regionName);
      });
      setConsolidatedRows(filteredConsolidatedData);

      const districtPromises = filteredConsolidatedData.map(async (region) => {
        const regionCode = resolveRegionCode(region);
        if (!regionCode) return ["", region.districts || {}];
        try {
          const response = await getAssessmentsByYearAndParentId({
            year,
            parentId: regionCode,
          });
          const responseRows = safeRows(response?.data);
          const mapFromResponse = {};
          responseRows.forEach((row) => {
            if (row?.districtCode) {
              mapFromResponse[row.districtCode] = row;
            }
          });
          return [regionCode, Object.keys(mapFromResponse).length > 0 ? mapFromResponse : region.districts || {}];
        } catch (error) {
          return [regionCode, region.districts || {}];
        }
      });

      const districtEntries = await Promise.all(districtPromises);
      setDistrictRowsByRegion(Object.fromEntries(districtEntries.filter(([regionCode]) => !!regionCode)));

      const regionalWriteUpEntries = await Promise.all(
        filteredConsolidatedData.map(async (region) => {
          const regionCode = resolveRegionCode(region);
          if (!regionCode) return ["", { generalComments: "", recommendations: "", scopeOfAssessment: "", generalOutcome: "" }];
          try {
            const response = await getWriteUpByYearAndLocation({ year, locationCode: regionCode });
            const writeUpRows = safeRows(response?.data);
            const generalCommentsRow =
              writeUpRows.find((row) => String(row?.sectionCommented || "").toUpperCase() === "OHLGS_GENERALCOMMENTS") ||
              writeUpRows.find((row) => String(row?.title || "").toLowerCase().includes("general comments")) ||
              null;
            const recommendationsRow =
              writeUpRows.find((row) => String(row?.sectionCommented || "").toUpperCase() === "OHLGS_RECOMMENDATIONS") ||
              writeUpRows.find((row) => String(row?.title || "").toLowerCase().includes("recommendation")) ||
              null;
            const scopeOfAssessmentRow =
              writeUpRows.find((row) => String(row?.sectionCommented || "").toUpperCase() === "OHLGS_SCOPEOFASSESSMENT") ||
              writeUpRows.find((row) => String(row?.title || "").toLowerCase().includes("scope of assessment")) ||
              null;
            const generalOutcomeRow =
              writeUpRows.find((row) => String(row?.sectionCommented || "").toUpperCase() === "OHLGS_GENERALOUTCOME") ||
              writeUpRows.find((row) => String(row?.title || "").toLowerCase().includes("general outcome")) ||
              null;
            return [
              regionCode,
              {
                generalComments: generalCommentsRow?.comments || "",
                recommendations: recommendationsRow?.comments || "",
                scopeOfAssessment: scopeOfAssessmentRow?.comments || "",
                generalOutcome: generalOutcomeRow?.comments || "",
              },
            ];
          } catch (_error) {
            return [regionCode, { generalComments: "", recommendations: "", scopeOfAssessment: "", generalOutcome: "" }];
          }
        }),
      );
      setRegionalWriteUpsByCode(Object.fromEntries(regionalWriteUpEntries.filter(([regionCode]) => !!regionCode)));

      const regionCodesForChapterFour = Array.from(
        new Set([
          ...filteredConsolidatedData.map((region) => resolveRegionCode(region)).filter(Boolean),
          ...regionCodesFromOrgTree,
        ]),
      );

      const chapterFourDataPromises = regionCodesForChapterFour.map(async (regionCode) => {
        if (!regionCode) return [];
        try {
          const response = await getAssessmentsByYearAndParentId({
            year,
            parentId: regionCode,
          });
          const rows = safeRows(response?.data);
          const regionName = orgIndex[regionCode]?.name || regionCode;
          return rows.map((row) => ({
            ...row,
            _sourceRegionCode: regionCode,
            _sourceRegionName: regionName,
          }));
        } catch (_error) {
          return [];
        }
      });
      const chapterFourRawRows = (await Promise.all(chapterFourDataPromises)).flat();
      const detailedRows = chapterFourRawRows.filter((row) => row?.kpi?.kpiId);
      const dedupedRowsById = {};
      detailedRows.forEach((row) => {
        const key = row?.contractPerformanceAssessmentId || `${row?.locationCode}-${row?.kpi?.kpiId}`;
        dedupedRowsById[key] = row;
      });
      const orderedChapterFourRows = Object.values(dedupedRowsById).sort((a, b) => {
        const kpaA = String(a?.kpi?.kpa?.kpaName || "");
        const kpaB = String(b?.kpi?.kpa?.kpaName || "");
        const kpaDiff = kpaA.localeCompare(kpaB);
        if (kpaDiff !== 0) return kpaDiff;
        return Number(a?.kpi?.kpiId || 0) - Number(b?.kpi?.kpiId || 0);
      });
      setChapterFourRows(orderedChapterFourRows);

      await loadNarrativeWriteUps(year);
      setHasLoaded(true);
      toast.success("Consolidated report loaded successfully");
    } catch (error) {
      console.error("Failed to load consolidated OHLGS report:", error);
      toast.error(error?.response?.data?.message || "Failed to load consolidated report");
      setConsolidatedRows([]);
      setDistrictRowsByRegion({});
      setHasLoaded(false);
    } finally {
      setIsLoading(false);
    }
  };

  const saveNarrativeWriteUps = async () => {
    if (!selectedYear?.value) return;
    setIsSavingWriteUps(true);
    const year = String(selectedYear.value);

    const payloads = [
      {
        id: generalIssuesWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_GENERAL_ISSUES",
        title: "OHLGS Consolidated General Issues and Observations",
        comments: generalIssues,
      },
      {
        id: dagiWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_DAGI",
        title: "OHLGS Consolidated DAGI Narrative",
        comments: dagiNarrative,
      },
      {
        id: executiveSummaryWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_EXECUTIVE_SUMMARY",
        title: "OHLGS Consolidated Executive Summary",
        comments: executiveSummary,
      },
      {
        id: introductionWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_INTRODUCTION",
        title: "OHLGS Consolidated Introduction",
        comments: introductionNarrative,
      },
      {
        id: chapterTwoMainWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_MAIN",
        title: "Performance Contract and implementation process in the PMS",
        comments: chapterTwoMainNarrative,
      },
      {
        id: phaseOneWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE1",
        title: "Phase one: Performance Planning",
        comments: phaseOneNarrative,
      },
      {
        id: phaseTwoWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE2",
        title: "Phase two: Progress Review",
        comments: phaseTwoNarrative,
      },
      {
        id: phaseThreeWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE3",
        title: "Phase three: Review and Appraisal",
        comments: phaseThreeNarrative,
      },
      {
        id: phaseFourWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE4",
        title: "Phase four: Decision mankig",
        comments: phaseFourNarrative,
      },
      {
        id: chapterThreeIntroWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_INTRO",
        title: `Monitoring and Verification on ${year} Annual Performance Evaluation of Rcc and MMDAs`,
        comments: chapterThreeIntro,
      },
      {
        id: chapterThreeTableWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_MV_TEAMS_TABLE",
        title: "M&V Teams and the regions they unertook the exercise",
        comments: JSON.stringify(mvTeamRows || []),
      },
      {
        id: chapterThreeObjectiveWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_OBJECTIVE",
        title: "Objective of the M&V exercise",
        comments: chapterThreeObjective,
      },
      {
        id: chapterThreeScopeMethodologyWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_SCOPE_METHODOLOGY",
        title: "Scope and Methology on the M&V exercise",
        comments: chapterThreeScopeMethodology,
      },
      {
        id: chapterFourSummaryWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_SUMMARY",
        title: "Detailed Finding Issues and Recommendations from the M&V teams - Summary",
        comments: chapterFourSummary,
      },
      {
        id: chapterFourScopeWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_SCOPE_OF_ASSIGNMENT",
        title: "Scope of Assignment",
        comments: chapterFourScopeOfAssignment,
      },
      {
        id: chapterFourOutcomeWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_OUTCOME",
        title: "General Outcome",
        comments: chapterFourGeneralOutcome,
      },
      {
        id: chapterFourObservationWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_OBSERVATION",
        title: "Obersevation",
        comments: chapterFourObservation,
      },
      {
        id: chapterFourRecommendationWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_RECOMMENDATION",
        title: "General Recommendation",
        comments: chapterFourGeneralRecommendation,
      },
      {
        id: chapterFourConclusionWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_CONCLUSION",
        title: "Chapter 4 Conclusion",
        comments: chapterFourConclusion,
      },
      {
        id: chapterSixIntroductionWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_INTRODUCTION",
        title: `Decision making on ${year} Annual Performance Evaluation Results for RCC and MMDA - Introduction`,
        comments: chapterSixIntroduction,
      },
      {
        id: chapterSixGuidingPrinciplesWriteUpId,
        sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_GUIDING_PRINCIPLES",
        title: "Guiding Principles for Decision Making",
        comments: chapterSixGuidingPrinciples,
      },
    ];

    try {
      for (const item of payloads) {
        await handleSaveSection(item, { showToast: false });
      }

      toast.success("Status Code: 201 Created. Narratives saved successfully.");
    } catch (error) {
      console.error("Failed to save consolidated write-ups:", error);
      toast.error(error?.response?.data?.message || "Failed to save narratives");
    } finally {
      setIsSavingWriteUps(false);
    }
  };

  const updateMvTeamRow = (index, field, value) => {
    setMvTeamRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    );
  };

  const addMvTeamRow = () => {
    setMvTeamRows((current) => [
      ...current,
      { teamComposition: "", mmdasInRegionsMonitored: "", rccsMonitored: "" },
    ]);
  };

  const removeMvTeamRow = (index) => {
    setMvTeamRows((current) => {
      if (current.length <= 1) {
        return [{ teamComposition: "", mmdasInRegionsMonitored: "", rccsMonitored: "" }];
      }
      return current.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const handleViewAllReportClick = () => {
    setViewAllReport((current) => !current);
  };

  const handleDownloadPdf = () => {
    const reportElement = reportContainerRef.current;
    if (!reportElement) return;

    const printStyle = document.createElement("style");
    printStyle.id = "ohlgs-consolidated-print-style";
    printStyle.textContent = `
      @media print {
        .print-hide {
          display: none !important;
        }
        #ohlgs-consolidated-report-root section.card:has(> div[class*="md:justify-between"]) {
          display: none !important;
        }
        #ohlgs-consolidated-report-root section.card:has(> div.flex.justify-end),
        #ohlgs-consolidated-report-root section.card:has(> div.mt-4.flex.justify-end) {
          display: none !important;
        }
        body * {
          visibility: hidden !important;
        }
        #ohlgs-consolidated-report-root,
        #ohlgs-consolidated-report-root * {
          visibility: visible !important;
        }
        #ohlgs-consolidated-report-root {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
        }
      }
    `;
    document.head.appendChild(printStyle);
    window.print();
    setTimeout(() => {
      const styleEl = document.getElementById("ohlgs-consolidated-print-style");
      if (styleEl) styleEl.remove();
    }, 300);
  };

  if (currentLocation?.level !== "national") {
    return (
      <div className="main-content">
        <div className="card">
          <h2 className="mb-2 text-xl font-semibold text-foreground">National Level Required</h2>
          <p className="text-muted-foreground">
            Contract Performance Assessment reporting is available at national level only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background ohlgs-consolidated-writeups">
      <style>
        {`
          .ohlgs-consolidated-writeups .ql-editor {
            width: 100%;
            max-width: none;
            min-height: 160px;
            overflow-wrap: anywhere;
          }
          .ohlgs-consolidated-writeups .ql-container {
            font-size: 0.95rem;
          }
        `}
      </style>
      <div id="ohlgs-consolidated-report-root" ref={reportContainerRef} className="main-content space-y-5 pb-6">
        <section className="card p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Contract Performance Assessment Consolidated</p>
              <h2 className="mt-1 text-lg font-semibold text-foreground">
                National Consolidated Report
                {selectedRegion?.value !== "all" ? ` — ${selectedRegion?.label}` : ""}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Load regional and district assessment results for the selected year and region.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={handleViewAllReportClick}>
                <Icon name="FileText" className="h-4 w-4" />
                {viewAllReport ? "View Active Chapter" : "View All Report"}
              </Button>
              {viewAllReport ? (
                <Button type="button" variant="outline" onClick={handleDownloadPdf}>
                  <Icon name="Download" className="h-4 w-4" />
                  Download PDF
                </Button>
              ) : null}
              <div className="w-[150px]">
                <Select
                  label="Year"
                  options={YEAR_OPTIONS}
                  value={selectedYear}
                  onChange={setSelectedYear}
                  placeholder="Year"
                />
              </div>
              <div className="w-[220px]">
                <Select
                  label="Region"
                  options={regionOptions}
                  value={selectedRegion}
                  onChange={setSelectedRegion}
                  placeholder="All Regions"
                  searchable
                />
              </div>
              <Button type="button" onClick={handleLoad} disabled={isLoading || !selectedYear?.value}>
                {isLoading ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Search" className="h-4 w-4" />}
                {isLoading ? "Loading..." : "Load Consolidated"}
              </Button>
            </div>
          </div>
        </section>

       


        {hasLoaded ? (
          <>
          {viewAllReport || activeChapter === 1 ? (
            <>
             <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                   Executive Summary
                  </h3>
                </section>
            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Executive Summary
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={executiveSummary}
                onChange={setExecutiveSummary}
                placeholder="Write the executive summary for Chapter One..."
              />
              {renderSendSectionButton({
                id: executiveSummaryWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_EXECUTIVE_SUMMARY",
                title: "OHLGS Consolidated Executive Summary",
                comments: executiveSummary,
              })}
            </section>

          <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Number of MMDAs and Their Performance Rating by Region ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">No</th>
                      <th className="border border-border px-3 py-2 text-left">Region</th>
                      <th className="border border-border px-3 py-2 text-left">Excellent</th>
                      <th className="border border-border px-3 py-2 text-left">Very Good</th>
                      <th className="border border-border px-3 py-2 text-left">Good</th>
                      <th className="border border-border px-3 py-2 text-left">Satisfactory</th>
                      <th className="border border-border px-3 py-2 text-left">Unsatisfactory</th>
                      <th className="border border-border px-3 py-2 text-left">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionalCategoryCounts.map((row, index) => (
                      <tr key={row.regionCode}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{row.regionName}</td>
                        <td className="border border-border px-3 py-2">{row.excellent}</td>
                        <td className="border border-border px-3 py-2">{row.veryGood}</td>
                        <td className="border border-border px-3 py-2">{row.good}</td>
                        <td className="border border-border px-3 py-2">{row.satisfactory}</td>
                        <td className="border border-border px-3 py-2">{row.unsatisfactory}</td>
                        <td className="border border-border px-3 py-2">{row.total}</td>
                      </tr>
                    ))}
                    <tr className="bg-muted/20 font-semibold">
                      <td className="border border-border px-3 py-2" colSpan={2}>
                        Overall Total
                      </td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.excellent}</td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.veryGood}</td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.good}</td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.satisfactory}</td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.unsatisfactory}</td>
                      <td className="border border-border px-3 py-2">{chapterFiveTotals.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>



            <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">The Performance of the RCCs in the ({selectedYear?.value}) Assessment</h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">No</th>
                      <th className="border border-border px-3 py-2 text-left">Region</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankedRegionRows.map((region, index) => (
                      <tr key={region.regionCode}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{region.displayName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(regionTotalScore(region)).toFixed(2)}</td>
                        <td className="border border-border px-3 py-2">{region.computedRank}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(classifyOhlgsScore(regionTotalScore(region)))}`}>
                          {classifyOhlgsScore(regionTotalScore(region))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>


              <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">The Performance of the MMDAs in the ({selectedYear?.value}) Assessment</h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">No</th>
                      <th className="border border-border px-3 py-2 text-left">District</th>
                      <th className="border border-border px-3 py-2 text-left">Region</th>
                      <th className="border border-border px-3 py-2 text-left">Score (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtRows.map((district, index) => (
                      <tr key={`${district.regionCode}-${district.districtCode}`}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{district.districtName}</td>
                        <td className="border border-border px-3 py-2">{district.regionName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                        <td className="border border-border px-3 py-2">{toOrdinal(index + 1)}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>


            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  General Issues and Observations
                </p>
              </div>
              <ReactQuill
              
                theme="snow"
                value={generalIssues}
                onChange={setGeneralIssues}
                placeholder="Capture general issues and observations before the performance tables..."
              />
              {renderSendSectionButton({
                id: generalIssuesWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_GENERAL_ISSUES",
                title: "OHLGS Consolidated General Issues and Observations",
                comments: generalIssues,
              })}
            </section>

            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Decision Making on the year {selectedYear?.value} 
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={decisionMakingIntroduction}
                onChange={setDecisionMakingIntroduction}
                placeholder="Write the introduction for decision making on annual performance evaluation results..."
              />
              {renderSendSectionButton({
                id: chapterSixIntroductionWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_INTRODUCTION",
                title: `Decision making on ${selectedYear?.value} Annual Performance Evaluation Results for RCC and MMDA - Introduction`,
                comments: decisionMakingIntroduction,
              })}
            </section>


            <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Best 10 MMDAs with Highest Scores ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">Reg</th>
                      <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topTenDistricts.map((district, index) => (
                      <tr key={`top-${district.districtCode}`}>
                        <td className="border border-border px-3 py-2">{regionAbbreviation(district.regionName)}</td>
                        <td className="border border-border px-3 py-2">{district.districtName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                        <td className="border border-border px-3 py-2">{toOrdinal(index + 1)}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>


            <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  MMDAs with Unsatisfactory Scores ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">Reg</th>
                      <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsatisfactoryDistricts.map((district) => (
                      <tr key={`unsat-${district.districtCode}`}>
                        <td className="border border-border px-3 py-2">{regionAbbreviation(district.regionName)}</td>
                        <td className="border border-border px-3 py-2">{district.districtName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                        <td className="border border-border px-3 py-2">{district.nationalRank || "-"}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>


            <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Best 3 RCCs with Highest Scores ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">Name of RCC</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topThreeRccs.map((region) => (
                      <tr key={`rcc-${region.regionCode}`}>
                        <td className="border border-border px-3 py-2">{region.displayName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(regionTotalScore(region)).toFixed(2)}</td>
                        <td className="border border-border px-3 py-2">{region.computedRank}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(classifyOhlgsScore(regionTotalScore(region)))}`}>
                          {classifyOhlgsScore(regionTotalScore(region))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Integrated District Agriculture Governance Index  (DAGI) Indicators
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={dagiNarrative}
                onChange={setDagiNarrative}
                placeholder="Capture DAGI narrative after the tables..."
              />
              {renderSendSectionButton({
                id: dagiWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_DAGI",
                title: "OHLGS Consolidated DAGI Narrative",
                comments: dagiNarrative,
              })}
            </section>

            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  General Recommendations and Way Forward
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={guidingPrinciples}
                onChange={setGuidingPrinciples}
                placeholder="Recommendations and Way Forward"
              />
              {renderSendSectionButton({
                id: chapterSixGuidingPrinciplesWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_GUIDING_PRINCIPLES",
                title: "Guiding Principles for Decision Making",
                comments: guidingPrinciples,
              })}
            </section>








            {/* <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Decision making on {selectedYear?.value} Annual Performance Evaluation Results for RCC and MMDA
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={decisionMakingIntroduction}
                onChange={setDecisionMakingIntroduction}
                placeholder="Write the introduction for decision making on annual performance evaluation results..."
              />
            </section> */}

            {/* <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Guiding Principles for Decision Making
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={guidingPrinciples}
                onChange={setGuidingPrinciples}
                placeholder="Outline the guiding principles for decision making based on performance evaluation results..."
              />
            </section> */}

            {/* <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Table 1: Top 3 RCC ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Name of RCC</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topThreeRccs.map((region, index) => (
                      <tr key={`table1-rcc-${region.regionCode}`}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{region.displayName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(regionTotalScore(region)).toFixed(2)}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(classifyOhlgsScore(regionTotalScore(region)))}`}>
                          {classifyOhlgsScore(regionTotalScore(region))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section> */}

            {/* <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Table 2: Top 10 MMDAs ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">Position</th>
                      <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                        <th className="border border-border px-3 py-2 text-left">Region</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topTenDistricts.map((district, index) => (
                      <tr key={`table2-mmda-${district.districtCode}`}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{district.districtName}</td>
                         <td className="border border-border px-3 py-2">{district.regionName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section> */}

            {/* <section className="card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-base font-semibold text-foreground">
                  Table 3: All MMDAs whose evaluation score are unsatisfactory ({selectedYear?.value})
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full border border-border text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="border border-border px-3 py-2 text-left">No</th>
                      <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                      <th className="border border-border px-3 py-2 text-left">Region</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                      <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsatisfactoryDistricts.map((district, index) => (
                      <tr key={`table3-unsat-${district.districtCode}`}>
                        <td className="border border-border px-3 py-2">{index + 1}</td>
                        <td className="border border-border px-3 py-2">{district.districtName}</td>
                        <td className="border border-border px-3 py-2">{district.regionName}</td>
                        <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                        <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                      </tr>
                    ))}
                    {unsatisfactoryDistricts.length === 0 && (
                      <tr>
                        <td colSpan="5" className="border border-border px-3 py-2 text-center text-muted-foreground">
                          No MMDAs with unsatisfactory performance found for {selectedYear?.value}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section> */}

          

<section className="card p-4 md:p-5">
                  <h2 className="text-base font-semibold text-foreground">
                    Chapter ONE: INTRODUCTION
                  </h2>
                </section>




            <section className="card p-4 md:p-5">
              <div className="mb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Introduction
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={introductionNarrative}
                onChange={setIntroductionNarrative}
                placeholder="Write introduction for the report..."
              />
              {renderSendSectionButton({
                id: introductionWriteUpId,
                sectionCommented: "OHLGS_CONSOLIDATED_INTRODUCTION",
                title: "OHLGS Consolidated Introduction",
                comments: introductionNarrative,
              })}
            </section>

            <section className="card p-4 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Button type="button" variant="outline" disabled>
                  <Icon name="ChevronLeft" className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                  <p className="mt-1 text-sm text-foreground">Chapter 1 of 6</p>
                </div>
                <Button type="button" onClick={() => setActiveChapter(2)}>
                  Next
                  <Icon name="ChevronRight" className="h-4 w-4" />
                </Button>
              </div>
            </section>

            <section className="card p-4 md:p-5">
              <div className="flex justify-end">
                <Button type="button" onClick={saveNarrativeWriteUps} disabled={isSavingWriteUps} className="print-hide">
                  {isSavingWriteUps ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                  {isSavingWriteUps ? "Saving..." : "Save Narratives"}
                </Button>
              </div>
            </section>
            </>
          ) : null}

            {viewAllReport || activeChapter === 2 ? (
              <>
                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Chapter Two: Performance Contract and implementation process in the performance management system
                  </h3>
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Performance Contract and implementation process in the PMS
                  </p>
                  <ReactQuill theme="snow" value={chapterTwoMainNarrative} onChange={setChapterTwoMainNarrative} />
                  {renderSendSectionButton({
                    id: chapterTwoMainWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_MAIN",
                    title: "Performance Contract and implementation process in the PMS",
                    comments: chapterTwoMainNarrative,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Phase one: Performance Planning
                  </p>
                  <ReactQuill theme="snow" value={phaseOneNarrative} onChange={setPhaseOneNarrative} />
                  {renderSendSectionButton({
                    id: phaseOneWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE1",
                    title: "Phase one: Performance Planning",
                    comments: phaseOneNarrative,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Phase two: Progress Review
                  </p>
                  <ReactQuill theme="snow" value={phaseTwoNarrative} onChange={setPhaseTwoNarrative} />
                  {renderSendSectionButton({
                    id: phaseTwoWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE2",
                    title: "Phase two: Progress Review",
                    comments: phaseTwoNarrative,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Phase three: Review and Appraisal
                  </p>
                  <ReactQuill theme="snow" value={phaseThreeNarrative} onChange={setPhaseThreeNarrative} />
                  {renderSendSectionButton({
                    id: phaseThreeWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE3",
                    title: "Phase three: Review and Appraisal",
                    comments: phaseThreeNarrative,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Phase four: Decision mankig
                  </p>
                  <ReactQuill theme="snow" value={phaseFourNarrative} onChange={setPhaseFourNarrative} />
                  {renderSendSectionButton({
                    id: phaseFourWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER2_PHASE4",
                    title: "Phase four: Decision mankig",
                    comments: phaseFourNarrative,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <Button type="button" variant="outline" onClick={() => setActiveChapter(1)}>
                      <Icon name="ChevronLeft" className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                      <p className="mt-1 text-sm text-foreground">Chapter 2 of 6</p>
                    </div>
                    <Button type="button" onClick={() => setActiveChapter(3)}>
                      Next
                      <Icon name="ChevronRight" className="h-4 w-4" />
                    </Button>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={saveNarrativeWriteUps} disabled={isSavingWriteUps} className="print-hide">
                      {isSavingWriteUps ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                      {isSavingWriteUps ? "Saving..." : "Save Chapter Narratives"}
                    </Button>
                  </div>
                </section>
              </>
            ) : null}

            {viewAllReport || activeChapter === 3 ? (
              <>
                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Chapter Three: Monitoring and Verification on {selectedYear?.value} Annual Performance Evaluation of Rcc and MMDAs
                  </h3>
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    MVAPERM Introduction
                  </p>
                  <ReactQuill theme="snow" value={chapterThreeIntro} onChange={setChapterThreeIntro} />
                  {renderSendSectionButton({
                    id: chapterThreeIntroWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_INTRO",
                    title: `Monitoring and Verification on ${selectedYear?.value} Annual Performance Evaluation of Rcc and MMDAs`,
                    comments: chapterThreeIntro,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      M&V Teams and the regions they undertook the exercise in the table below
                    </p>
                    <Button type="button" variant="outline" onClick={addMvTeamRow} className="print-hide">
                      <Icon name="Plus" className="h-4 w-4" />
                      Add Row
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left align-top">No</th>
                          <th className="border border-border px-3 py-2 text-left align-top">Team Composition</th>
                          <th className="border border-border px-3 py-2 text-left align-top">MMDAs in Regions Monitored</th>
                          <th className="border border-border px-3 py-2 text-left align-top">RCCSs Monitored</th>
                          <th className="border border-border px-3 py-2 text-left print-hide">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mvTeamRows.map((row, index) => (
                          <tr key={`mv-team-${index}`}>
                            <td className="border border-border px-3 py-2 align-top">{index + 1}</td>
                            <td className="border border-border px-3 py-2 align-top print-hide min-w-[260px]">
                              <textarea
                                value={row.teamComposition}
                                onChange={(event) => updateMvTeamRow(index, "teamComposition", event.target.value)}
                                rows={3}
                                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm leading-5 resize-y whitespace-pre-wrap"
                                placeholder="Enter team composition"
                              />
                            </td>
                            <td className="border border-border px-3 py-2 align-top min-w-[260px]">
                              <textarea
                                value={row.mmdasInRegionsMonitored}
                                onChange={(event) => updateMvTeamRow(index, "mmdasInRegionsMonitored", event.target.value)}
                                rows={3}
                                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm leading-5 resize-y whitespace-pre-wrap"
                                placeholder="Enter monitored MMDAs/regions"
                              />
                            </td>
                            <td className="border border-border px-3 py-2 align-top min-w-[220px]">
                              <textarea
                                value={row.rccsMonitored}
                                onChange={(event) => updateMvTeamRow(index, "rccsMonitored", event.target.value)}
                                rows={3}
                                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm leading-5 resize-y whitespace-pre-wrap"
                                placeholder="Enter monitored RCCSs"
                              />
                            </td>
                            <td className="border border-border px-3 py-2 align-top">
                              <Button type="button" variant="ghost" onClick={() => removeMvTeamRow(index)}>
                                <Icon name="Trash2" className="h-4 w-4" />
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Objective of the M&V exercise
                  </p>
                  <ReactQuill theme="snow" value={chapterThreeObjective} onChange={setChapterThreeObjective} />
                  {renderSendSectionButton({
                    id: chapterThreeObjectiveWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_OBJECTIVE",
                    title: "Objective of the M&V exercise",
                    comments: chapterThreeObjective,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Scope and Methology on the M&V exercise
                  </p>
                  <ReactQuill theme="snow" value={chapterThreeScopeMethodology} onChange={setChapterThreeScopeMethodology} />
                  {renderSendSectionButton({
                    id: chapterThreeScopeMethodologyWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER3_SCOPE_METHODOLOGY",
                    title: "Scope and Methology on the M&V exercise",
                    comments: chapterThreeScopeMethodology,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <Button type="button" variant="outline" onClick={() => setActiveChapter(2)}>
                      <Icon name="ChevronLeft" className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                      <p className="mt-1 text-sm text-foreground">Chapter 3 of 6</p>
                    </div>
                    <Button type="button" onClick={() => setActiveChapter(4)}>
                      Next
                      <Icon name="ChevronRight" className="h-4 w-4" />
                    </Button>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={saveNarrativeWriteUps} disabled={isSavingWriteUps} className="print-hide">
                      {isSavingWriteUps ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                      {isSavingWriteUps ? "Saving..." : "Save Chapter Narratives"}
                    </Button>
                  </div>
                </section>
              </>
            ) : null}

            {viewAllReport || activeChapter === 4 ? (
              <>
                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Chapter 4: Detailed Finding Issues and Recommendations from the M&V teams
                  </h3>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      Table: Detailed Analysis / Findings by KPA and KPI
                    </h3>
                  </div>
                  <div className="space-y-6 p-4">
                    {chapterFourTablesByRegion.map((regionGroup) => {
                      const regionWriteUp = regionalWriteUpsByCode[regionGroup.regionCode];
                      const hasRegionWriteUp = Boolean(
                        regionWriteUp?.scopeOfAssessment ||
                        regionWriteUp?.generalOutcome ||
                        regionWriteUp?.generalComments ||
                        regionWriteUp?.recommendations,
                      );

                      return (
                      <div key={`chapter4-region-group-${regionGroup.regionCode}`} className="space-y-5">
                        {hasRegionWriteUp ? (
                          <RegionalMvSummaryCards
                            regionName={regionGroup.regionName}
                            writeUp={regionWriteUp}
                          />
                        ) : null}

                        {regionGroup.regionalRows.length > 0 ? (
                          <div className="space-y-4">
                            <p className="text-sm font-semibold text-foreground">
                              Detailed Analysis / Findings of each Indicator for {regionGroup.regionName}
                            </p>

                            <div className="overflow-x-auto rounded-lg border border-border">
                              <table className="min-w-full border-collapse text-sm">
                                <thead className="bg-muted/30">
                                  <tr>
                                    <th className="border border-border px-3 py-2 text-left">Source Location</th>
                                    <th className="border border-border px-3 py-2 text-left">KPA</th>
                                    <th className="border border-border px-3 py-2 text-left">KPI</th>
                                    <th className="border border-border px-3 py-2 text-left">Key Issues</th>
                                    <th className="border border-border px-3 py-2 text-left">Observations</th>
                                    <th className="border border-border px-3 py-2 text-left">Recommendations</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {regionGroup.regionalRows.map((row) => (
                                    <tr
                                      key={`chapter4-region-row-${regionGroup.regionCode}-${row.contractPerformanceAssessmentId || `${row.locationCode}-${row.kpi?.kpiId}`}`}
                                    >
                                      <td className="border border-border px-3 py-2 align-top text-left">
                                        {sourceLocationName(row, orgIndex)}
                                      </td>
                                      <td className="border border-border px-3 py-2 align-top text-left">{row?.kpi?.kpa?.kpaName || "-"}</td>
                                      <td className="border border-border px-3 py-2 align-top text-left">{row?.kpi?.kpiName || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.keyIssues || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.observation || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.recommendation || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}

                        {regionGroup.districts.map((districtGroup) => (
                          <div key={`chapter4-district-group-${regionGroup.regionCode}-${districtGroup.districtCode}`} className="space-y-3">
                            <p className="text-sm font-semibold text-foreground">
                              {regionGroup.regionName} {"=>"} {districtGroup.districtName}
                            </p>
                            <div className="overflow-x-auto rounded-lg border border-border">
                              <table className="min-w-full border-collapse text-sm">
                                <thead className="bg-muted/30">
                                  <tr>
                                    <th className="border border-border px-3 py-2 text-left">Source Location</th>
                                    <th className="border border-border px-3 py-2 text-left">KPA</th>
                                    <th className="border border-border px-3 py-2 text-left">KPI</th>
                                    <th className="border border-border px-3 py-2 text-left">Key Issues</th>
                                    <th className="border border-border px-3 py-2 text-left">Observations</th>
                                    <th className="border border-border px-3 py-2 text-left">Recommendations</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {districtGroup.rows.map((row) => (
                                    <tr
                                      key={`chapter4-district-row-${regionGroup.regionCode}-${districtGroup.districtCode}-${row.contractPerformanceAssessmentId || `${row.locationCode}-${row.kpi?.kpiId}`}`}
                                    >
                                      <td className="border border-border px-3 py-2 align-top text-left">
                                        {sourceLocationName(row, orgIndex)}
                                      </td>
                                      <td className="border border-border px-3 py-2 align-top text-left">{row?.kpi?.kpa?.kpaName || "-"}</td>
                                      <td className="border border-border px-3 py-2 align-top text-left">{row?.kpi?.kpiName || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.keyIssues || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.observation || "-"}</td>
                                      <td className={CHAPTER4_NARRATIVE_CELL}>{row?.recommendation || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                      );
                    })}
                  </div>
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    General Issues
                  </p>
                  <ReactQuill theme="snow" value={generalIssues} onChange={setGeneralIssues} />
                  {renderSendSectionButton({
                    id: generalIssuesWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_GENERAL_ISSUES",
                    title: "General Issues and Observations",
                    comments: generalIssues,
                  })}
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Impressions
                  </p>
                  <ReactQuill theme="snow" value={chapterFourObservation} onChange={setChapterFourObservation} />
                  {renderSendSectionButton({
                    id: chapterFourObservationWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_OBSERVATION",
                    title: "Impressions",
                    comments: chapterFourObservation,
                  })}
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Best Practices
                  </p>
                  <ReactQuill theme="snow" value={chapterFourSummary} onChange={setChapterFourSummary} />
                  {renderSendSectionButton({
                    id: chapterFourSummaryWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_SUMMARY",
                    title: "Best Practices",
                    comments: chapterFourSummary,
                  })}
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Integrated District Agriculture Governance Index (DAGI) Indicators
                  </p>
                  <ReactQuill theme="snow" value={dagiNarrative} onChange={setDagiNarrative} />
                  {renderSendSectionButton({
                    id: dagiWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_DAGI",
                    title: "OHLGS Consolidated DAGI Narrative",
                    comments: dagiNarrative,
                  })}
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Recommendation
                  </p>
                  <ReactQuill theme="snow" value={chapterFourGeneralRecommendation} onChange={setChapterFourGeneralRecommendation} />
                  {renderSendSectionButton({
                    id: chapterFourRecommendationWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_GENERAL_RECOMMENDATION",
                    title: "Recommendation",
                    comments: chapterFourGeneralRecommendation,
                  })}
                </section>

            <section className="card p-4 md:p-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Conclusion
                  </p>
                  <ReactQuill theme="snow" value={chapterFourConclusion} onChange={setChapterFourConclusion} />
                  {renderSendSectionButton({
                    id: chapterFourConclusionWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER4_CONCLUSION",
                    title: "Conclusion",
                    comments: chapterFourConclusion,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <Button type="button" variant="outline" onClick={() => setActiveChapter(3)}>
                      <Icon name="ChevronLeft" className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                      <p className="mt-1 text-sm text-foreground">Chapter 4 of 6</p>
                    </div>
                    <Button type="button" onClick={() => setActiveChapter(5)}>
                      Next
                      <Icon name="ChevronRight" className="h-4 w-4" />
                    </Button>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={saveNarrativeWriteUps} disabled={isSavingWriteUps} className="print-hide">
                      {isSavingWriteUps ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                      {isSavingWriteUps ? "Saving..." : "Save Chapter Narratives"}
                    </Button>
                  </div>
                </section>
              </>
            ) : null}

            {viewAllReport || activeChapter === 5 ? (
              <>
                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Chapter 5: Results for the {selectedYear?.value} Annual Performance Evaluation for RCC and MMDA
                  </h3>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      5.1 Results & Ranking for the {selectedYear?.value} Annual Performance Evaluation of RCCs
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">No</th>
                          <th className="border border-border px-3 py-2 text-left">Name of RCC</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                          <th className="border border-border px-3 py-2 text-left">Position</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankedRegionRows.map((region, index) => (
                          <tr key={`chapter5-rcc-${region.regionCode}`}>
                            <td className="border border-border px-3 py-2">{index + 1}</td>
                            <td className="border border-border px-3 py-2">{region.displayName}</td>
                            <td className="border border-border px-3 py-2">{normalizePercent(regionTotalScore(region)).toFixed(2)}</td>
                            <td className="border border-border px-3 py-2">{region.computedRank}</td>
                            <td className={`border border-border px-3 py-2 ${performanceClass(classifyOhlgsScore(regionTotalScore(region)))}`}>
                              {classifyOhlgsScore(regionTotalScore(region))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      5.2 Results & Ranking for the {selectedYear?.value} Annual Performance Evaluation of MMDAs
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">No</th>
                          <th className="border border-border px-3 py-2 text-left">REG</th>
                          <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                          <th className="border border-border px-3 py-2 text-left">Position</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {districtRows.map((district, index) => (
                          <tr key={`chapter5-mmda-${district.districtCode}-${index}`}>
                            <td className="border border-border px-3 py-2">{index + 1}</td>
                            <td className="border border-border px-3 py-2">{regionAbbreviation(district.regionName)}</td>
                            <td className="border border-border px-3 py-2">{district.districtName}</td>
                            <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                            <td className="border border-border px-3 py-2">{toOrdinal(index + 1)}</td>
                            <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      5.3 Statistics for Performance Evaluation Rating of MMDAs by Region
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">No</th>
                          <th className="border border-border px-3 py-2 text-left">REGION</th>
                          <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Excellent"</th>
                          <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Very Good"</th>
                          <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Good"</th>
                          <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Satisfactory"</th>
                          <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Unsatisfactory"</th>
                          <th className="border border-border px-3 py-2 text-left">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {regionalCategoryCounts.map((row, index) => (
                          <tr key={`chapter5-stats-${row.regionCode}`}>
                            <td className="border border-border px-3 py-2">{index + 1}</td>
                            <td className="border border-border px-3 py-2">{row.regionName}</td>
                            <td className="border border-border px-3 py-2">{row.excellent}</td>
                            <td className="border border-border px-3 py-2">{row.veryGood}</td>
                            <td className="border border-border px-3 py-2">{row.good}</td>
                            <td className="border border-border px-3 py-2">{row.satisfactory}</td>
                            <td className="border border-border px-3 py-2">{row.unsatisfactory}</td>
                            <td className="border border-border px-3 py-2">{row.total}</td>
                          </tr>
                        ))}
                        <tr className="bg-muted/20 font-semibold">
                          <td className="border border-border px-3 py-2" colSpan={2}>Total</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.excellent}</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.veryGood}</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.good}</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.satisfactory}</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.unsatisfactory}</td>
                          <td className="border border-border px-3 py-2">{chapterFiveTotals.total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">Regional Dissagregation</h3>
                  </div>
                  <div className="p-4">
                    <div className="h-[340px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chapterFiveBarChartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="region" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="excellent" name='No. of MMDAs scored "Excellent"' fill={CHART_COLORS.excellent} />
                          <Bar dataKey="veryGood" name='No. of MMDAs scored "Very Good"' fill={CHART_COLORS.veryGood} />
                          <Bar dataKey="good" name='No. of MMDAs scored "Good"' fill={CHART_COLORS.good} />
                          <Bar dataKey="satisfactory" name='No. of MMDAs scored "Satisfactory"' fill={CHART_COLORS.satisfactory} />
                          <Bar dataKey="unsatisfactory" name='No. of MMDAs scored "Unsatisfactory"' fill={CHART_COLORS.unsatisfactory} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      Overall Performance Ranking by {chapterFiveTotals.total} MMDAs
                    </h3>
                  </div>
                  <div className="grid gap-4 p-4 lg:grid-cols-[1fr_240px]">
                    <div className="h-[320px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chapterFivePieData}
                            cx="50%"
                            cy="50%"
                            outerRadius={110}
                            dataKey="value"
                            nameKey="name"
                            label={({ percent }) => `${(percent * 1).toFixed(0)}%`}
                          >
                            {chapterFivePieData.map((entry) => (
                              <Cell key={`chapter5-pie-${entry.name}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {chapterFivePieData.map((entry) => (
                        <div key={`legend-${entry.name}`} className="flex items-start gap-2 text-sm">
                          <span className="mt-1 h-3 w-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                          <span>
                            {entry.name}: {entry.value} ({entry.percent}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    5.4 Performance Evaluation results of MMDAs by Region
                  </h3>
                </section>

                {chapterFiveRegionBreakdown.map((regionBlock, regionIndex) => (
                  <section key={`chapter5-region-${regionBlock.regionCode}`} className="card overflow-hidden p-0">
                    <div className="border-b border-border px-5 py-4">
                      <h4 className="text-sm font-semibold text-foreground">
                        5.4.{regionIndex + 1} Performance Evaluation Results of MMDAs - {regionBlock.regionName} ({regionBlock.regionAbbrev})
                      </h4>
                    </div>
                    <div className="overflow-x-auto p-4">
                      <table className="min-w-full border border-border text-sm">
                        <thead className="bg-muted/30">
                          <tr>
                            <th className="border border-border px-3 py-2 text-left">No</th>
                            <th className="border border-border px-3 py-2 text-left">REG</th>
                            <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                            <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                            <th className="border border-border px-3 py-2 text-left">National Position</th>
                            <th className="border border-border px-3 py-2 text-left">Reg Position</th>
                            <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {regionBlock.districts.map((district, districtIndex) => (
                            <tr key={`chapter5-region-row-${regionBlock.regionCode}-${district.districtCode}`}>
                              <td className="border border-border px-3 py-2">{districtIndex + 1}</td>
                              <td className="border border-border px-3 py-2">{regionBlock.regionAbbrev}</td>
                              <td className="border border-border px-3 py-2">{district.districtName}</td>
                              <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                              <td className="border border-border px-3 py-2">{district.nationalPosition ? toOrdinal(district.nationalPosition) : "-"}</td>
                              <td className="border border-border px-3 py-2">{toOrdinal(district.regionalPosition)}</td>
                              <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="overflow-x-auto p-4 pt-0">
                      <table className="min-w-full border border-border text-sm">
                        <thead className="bg-muted/30">
                          <tr>
                            <th className="border border-border px-3 py-2 text-left">REGION</th>
                            <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Excellent"</th>
                            <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Very Good"</th>
                            <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Good"</th>
                            <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Satisfactory"</th>
                            <th className="border border-border px-3 py-2 text-left">No. of MMDAs scored "Unsatisfactory"</th>
                            <th className="border border-border px-3 py-2 text-left">TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-border px-3 py-2">{regionBlock.regionName}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.counts.excellent}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.counts.veryGood}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.counts.good}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.counts.satisfactory}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.counts.unsatisfactory}</td>
                            <td className="border border-border px-3 py-2">{regionBlock.total}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}

                <section className="card p-4 md:p-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <Button type="button" variant="outline" onClick={() => setActiveChapter(4)}>
                      <Icon name="ChevronLeft" className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                      <p className="mt-1 text-sm text-foreground">Chapter 5 of 6</p>
                    </div>
                    <Button type="button" onClick={() => setActiveChapter(6)}>
                      Next
                      <Icon name="ChevronRight" className="h-4 w-4" />
                    </Button>
                  </div>
                </section>
              </>
            ) : null}

            {viewAllReport || activeChapter === 6 ? (
              <>
                <section className="card p-4 md:p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Chapter Six: Decision making on {selectedYear?.value} Annual Performance Evaluation Results for RCC and MMDA
                  </h3>
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mb-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      Introduction
                    </p>
                  </div>
                  <ReactQuill
                    theme="snow"
                    value={chapterSixIntroduction}
                    onChange={setChapterSixIntroduction}
                    placeholder="Write the introduction for decision making on annual performance evaluation results..."
                  />
                  {renderSendSectionButton({
                    id: chapterSixIntroductionWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_INTRODUCTION",
                    title: `Decision making on ${selectedYear?.value} Annual Performance Evaluation Results for RCC and MMDA - Introduction`,
                    comments: chapterSixIntroduction,
                  })}
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mb-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      Guiding Principles for Decision Making
                    </p>
                  </div>
                  <ReactQuill
                    theme="snow"
                    value={chapterSixGuidingPrinciples}
                    onChange={setChapterSixGuidingPrinciples}
                    placeholder="Outline the guiding principles for decision making based on performance evaluation results..."
                  />
                  {renderSendSectionButton({
                    id: chapterSixGuidingPrinciplesWriteUpId,
                    sectionCommented: "OHLGS_CONSOLIDATED_CHAPTER6_GUIDING_PRINCIPLES",
                    title: "Guiding Principles for Decision Making",
                    comments: chapterSixGuidingPrinciples,
                  })}
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      Table 1: Top 3 RCC ({selectedYear?.value})
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">Position</th>
                          <th className="border border-border px-3 py-2 text-left">Name of RCC</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topThreeRccs.map((region, index) => (
                          <tr key={`table1-rcc-${region.regionCode}`}>
                            <td className="border border-border px-3 py-2">{region.computedRank || toOrdinal(index + 1)}</td>
                            <td className="border border-border px-3 py-2">{region.displayName}</td>
                            <td className="border border-border px-3 py-2">{normalizePercent(regionTotalScore(region)).toFixed(2)}</td>
                            <td className={`border border-border px-3 py-2 ${performanceClass(classifyOhlgsScore(regionTotalScore(region)))}`}>
                              {classifyOhlgsScore(regionTotalScore(region))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      Table 2: Top 10 MMDAs ({selectedYear?.value})
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">Position</th>
                          <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                          <th className="border border-border px-3 py-2 text-left">Region</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topTenDistricts.map((district, index) => (
                          <tr key={`table2-mmda-${district.districtCode}`}>
                            <td className="border border-border px-3 py-2">{toOrdinal(index + 1)}</td>
                            <td className="border border-border px-3 py-2">{district.districtName}</td>
                            <td className="border border-border px-3 py-2">{district.regionName}</td>
                            <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                            <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card overflow-hidden p-0">
                  <div className="border-b border-border px-5 py-4">
                    <h3 className="text-base font-semibold text-foreground">
                      Table 3: All MMDAs whose evaluation score are unsatisfactory ({selectedYear?.value})
                    </h3>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="min-w-full border border-border text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="border border-border px-3 py-2 text-left">Position</th>
                          <th className="border border-border px-3 py-2 text-left">Name of MMDA</th>
                          <th className="border border-border px-3 py-2 text-left">Region</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Rating (%)</th>
                          <th className="border border-border px-3 py-2 text-left">Overall Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsatisfactoryDistricts.map((district, index) => (
                          <tr key={`table3-unsat-${district.districtCode}`}>
                            <td className="border border-border px-3 py-2">{toOrdinal(index + 1)}</td>
                            <td className="border border-border px-3 py-2">{district.districtName}</td>
                            <td className="border border-border px-3 py-2">{district.regionName}</td>
                            <td className="border border-border px-3 py-2">{normalizePercent(district.totalScore).toFixed(2)}</td>
                            <td className={`border border-border px-3 py-2 ${performanceClass(district.performance)}`}>{district.performance}</td>
                          </tr>
                        ))}
                        {unsatisfactoryDistricts.length === 0 && (
                          <tr>
                            <td colSpan="5" className="border border-border px-3 py-2 text-center text-muted-foreground">
                              No MMDAs with unsatisfactory performance found for {selectedYear?.value}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card p-4 md:p-5">
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={saveNarrativeWriteUps} disabled={isSavingWriteUps} className="print-hide">
                      {isSavingWriteUps ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                      {isSavingWriteUps ? "Saving..." : "Save Chapter Narratives"}
                    </Button>
                  </div>
                </section>

                <section className="card p-4 md:p-5 print-hide">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <Button type="button" variant="outline" onClick={() => setActiveChapter(5)}>
                      <Icon name="ChevronLeft" className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Chapter Navigation</p>
                      <p className="mt-1 text-sm text-foreground">Chapter 6 of 6</p>
                    </div>
                    <Button type="button" disabled>
                      Next
                      <Icon name="ChevronRight" className="h-4 w-4" />
                    </Button>
                  </div>
                </section>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
